import { isTopic } from '@tunein/web-common';
import WebMediaPlayer, { MediaTailorManager, } from '@tunein/web-media-player';
import { propagateEvents, wait } from '@tunein/web-utils';
import { noop } from 'es-toolkit';
import { forEach } from 'es-toolkit/compat';
import EventEmitter from 'eventemitter3';
import { v4 as uuidv4 } from 'uuid';
import { AdPlayer } from '../AdPlayer';
import { StreamDataManager } from '../StreamDataManager';
import { BOOST_ENABLED, BOOST_INTRO_AUDIO, BOOST_INTRO_PLAYSESSIONFREQCAP_COUNT, BOOST_OUTRO_AUDIO, BOOST_OUTRO_PLAYSESSIONFREQCAP_COUNT, PLAYER_ICY_METADATA_ENABLED, PLAYER_TIMEOUT_MS, REJECTED_STREAMS_REASON_BLOCKLIST, } from '../constants';
import { appSessionDataProvider, localizationsProvider, } from '../providers';
import { api, getExperiment, player } from '../singletons';
import { debug, toggleDebug } from '../utils';
import { AppReporter } from './AppReporter';
import { PlayerReporter } from './PlayerReporter';
import { generateListenId } from './utils';
export class PlayerManager extends EventEmitter {
    #options;
    #ssaiManager;
    #playerReporter;
    #appReporter;
    #streamDataManager;
    #adPlayer;
    #playerEventListeners;
    #listenId = 0;
    #guideItem;
    #loadOptions;
    #isLoading = null;
    #isFailed = null;
    #boostState;
    #boostConfig;
    #metadata = {};
    constructor(options) {
        super();
        this.#options = {
            ...options,
            autoplayNextTopic: true,
        };
        this.#appReporter = new AppReporter();
    }
    #registerEvents() {
        forEach(this.#playerEventListeners, (cb, event) => {
            player.off(event, cb);
            player.on(event, cb);
        });
    }
    #deregisterEvents() {
        forEach(this.#playerEventListeners, (cb, event) => player.off(event, cb));
    }
    #setupWebMediaPlayer(playerOptions) {
        const playerInstance = new WebMediaPlayer({
            ...playerOptions,
            timeoutMs: getExperiment(PLAYER_TIMEOUT_MS, 10_000),
            enableIcyMetadata: getExperiment(PLAYER_ICY_METADATA_ENABLED),
            hlsLiveSeekFragTagKey: 'EXT-X-COM-TUNEIN-AVAIL-DUR',
            // advertising option is set by AdPlayer dynamically on init
        });
        this.#ssaiManager = new MediaTailorManager();
        playerInstance.setSSAIManager(this.#ssaiManager);
        player.init(playerInstance);
    }
    #onError = (event) => {
        this.emit('error', {
            ...event,
            hadOnlyHttpStreams: this.streamData?.hadOnlyHttpStreams,
        });
    };
    #onEnded = async () => {
        if (!this.#options.autoplayNextTopic) {
            return;
        }
        // Waiting to allow `ended` listener in PlayerReporter to go first and report the end event.
        await wait();
        if (isTopic(this.#metadata.secondaryGuideId) && this.hasNext()) {
            await this.playNext(true);
            return;
        }
    };
    #onMetadata = (metadata) => {
        try {
            this.#metadata = metadata;
            this.emit('metadata', this.#metadata);
        }
        catch (error) {
            debug('Error in metadata event handler', error);
        }
    };
    #generateListenId() {
        this.#appReporter.previousListenId = this.#listenId;
        this.#listenId = generateListenId();
        this.#updateReportingContext();
        return this.#listenId;
    }
    #updateReportingContext() {
        const { guideId = '', parentGuideId = '' } = this.#guideItem || {};
        api.updateReportingContext({
            listeningInfo: {
                listenId: this.#listenId.toString(),
                guideId,
                parentGuideId,
            },
        });
    }
    #transformGuideItem(guideItem) {
        const { playableGuideId, guideId } = guideItem;
        return { ...guideItem, guideId: playableGuideId || guideId };
    }
    #transformLoadOptions(guideItem, options) {
        this.#guideItem = this.#transformGuideItem(guideItem);
        this.#loadOptions = {
            isUserInitiated: true,
            ...options,
        };
    }
    /**
     * When the Tune API denies (aka rejects) a request for streams, it provides a rejectReason enum value.
     * Applications can define a Config Setting that maps the rejectReason to a translation key.
     * This function translates that key if it exists and allows Player Manager to short-circuit the load call and emit
     * a metadata event with the rejectReason translation string.
     */
    #getStreamRejectedReason(streams) {
        const reasonType = streams[0]?.rejectReason;
        if (!reasonType) {
            return;
        }
        const reasonTypeToTranslationKeyMap = api.getConfigSetting(REJECTED_STREAMS_REASON_BLOCKLIST, {});
        api.telemetry.reportSandbox({
            category: 'player',
            action: 'reject',
            props: { errorType: reasonType },
        });
        const reasonTranslationKey = reasonTypeToTranslationKeyMap[reasonType];
        if (reasonTranslationKey) {
            return localizationsProvider.get(reasonTranslationKey);
        }
    }
    async #load(guideItem, options) {
        const { guideId: previousGuideId } = this.#guideItem || {};
        this.#transformLoadOptions(guideItem, options);
        if (!(this.#guideItem && this.#loadOptions)) {
            return;
        }
        this.stop();
        // Plays a silent audio stream immediately to avoid the browser's autoplay restrictions from blocking playback.
        // This is a workaround for browsers that require user interaction before playing media (e.g., Chrome on iOS).
        player.playBlankMp3();
        this.#isLoading = true;
        const listenId = this.#generateListenId();
        this.emit('loadstart');
        const isReplayingBoostStream = Boolean(getExperiment(BOOST_ENABLED) &&
            this.#guideItem.guideId === previousGuideId &&
            this.#metadata.boost &&
            player.isBoostOrIntroStream);
        if (isReplayingBoostStream) {
            await this.#playBoostTransition(isReplayingBoostStream);
            // if load request was interrupted by a new one, bail out
            if (listenId !== this.#listenId) {
                return;
            }
            this.emit('loadstart');
        }
        const { guideId } = this.#guideItem;
        this.#playerReporter.startSession({
            guideItem: this.#guideItem,
            listenId: this.#listenId,
            isUserInitiated: this.#loadOptions.isUserInitiated,
            isAutoPlayingNextStream: this.#loadOptions.isAutoPlayingNextStream,
        });
        // Wait for app session data provider to be ready, which includes waiting for consent data to resolve, if applicable
        await appSessionDataProvider.ready;
        try {
            await this.#streamDataManager.fetchStreams({
                guideItem: this.#guideItem,
                listenId: this.#listenId,
                loadOptions: options,
                isReplayingBoostStream,
            });
            // if load request was interrupted by a new one, bail out
            if (listenId !== this.#listenId) {
                return;
            }
            const { streams, boostStream, prerollStream, subscriptionRequiredStream, nowPlaying, hadOnlyHttpStreams, externalPlayerUrl, } = this.streamData;
            const rejectReason = this.#getStreamRejectedReason(streams);
            if (rejectReason) {
                this.#metadata.rejectReason = rejectReason;
                this.stop();
                this.emit('metadata', this.#metadata);
                return;
            }
            if (subscriptionRequiredStream) {
                await player.load(subscriptionRequiredStream);
                return streams;
            }
            this.#adPlayer.setSessionConfig({ guideId, nowPlaying, prerollStream });
            if (!isReplayingBoostStream &&
                !this.#loadOptions.skipPreroll &&
                (prerollStream || this.#adPlayer.canPlayPreroll)) {
                await this.#adPlayer.requestPreroll();
                // If the load request was interrupted by a new one during ad break, we should bail
                if (listenId !== this.#listenId) {
                    return;
                }
            }
            // The app can access the external player URL from the streamData getter
            if (externalPlayerUrl) {
                this.#isLoading = null;
                return;
            }
            // Allow position override for topics only
            if (streams[0] &&
                this.#loadOptions.position &&
                isTopic(this.#guideItem.guideId)) {
                streams[0].position = this.#loadOptions.position;
            }
            this.#registerEvents();
            this.#playerReporter.onStartStreamLoad(streams, hadOnlyHttpStreams);
            // Player Manager has finished its "loading" work. Resetting the flag to allow the player to represent its state.
            this.#isLoading = null;
            await player.load(isReplayingBoostStream ? boostStream : streams);
            return streams;
        }
        catch (error) {
            this.#isLoading = null;
            this.#isFailed = true;
            this.#playerReporter.reportUnknownLoadError(error);
            // TODO: setting isLastSource to true in order to fulfill the contract that this flag indicates a total load failure.
            //  We'll refactor error handling to be more consistent and clear on a future major release.
            this.emit('error', {
                error: { message: error.message, fatal: true },
                isLastSource: true,
            });
        }
    }
    #resetBoostState() {
        this.#boostState = {
            playedIntroCount: 0,
            playedOutroCount: 0,
        };
    }
    async #playBoostTransition(isReplayingBoostStream = false) {
        // user can switch back to directory station during boost stream or boost intro stream
        const useOutroStream = !isReplayingBoostStream && player.isBoostOrIntroStream;
        const boostTransitionStream = useOutroStream
            ? this.#boostConfig.outroStreamSource
            : this.#boostConfig.introStreamSource;
        const maxCount = useOutroStream
            ? this.#boostConfig.outroMaxCount
            : this.#boostConfig.introMaxCount;
        const playedCount = useOutroStream
            ? 'playedOutroCount'
            : 'playedIntroCount';
        if (!boostTransitionStream?.url ||
            // maxCount === 0 means the stream should not be played
            maxCount === 0 ||
            // maxCount can be -1, which means infinite plays
            (maxCount > 0 && this.#boostState[playedCount] >= maxCount)) {
            return;
        }
        const { promise, resolve } = Promise.withResolvers();
        this.#deregisterEvents();
        this.#streamDataManager.sendBoostMetadata({
            isBoostOrIntroStream: !useOutroStream,
            isBoostTransition: true,
        });
        const onDone = () => {
            player.off('ended', onEnded);
            player.off('error', onError);
            this.#registerEvents();
            resolve();
        };
        const onEnded = () => {
            this.#boostState[playedCount]++;
            onDone();
        };
        const onError = (event) => {
            if (event.error.fatal) {
                onDone();
            }
        };
        player.once('ended', onEnded);
        player.once('error', onError);
        await player.load(boostTransitionStream);
        if (this.isLoaded()) {
            await player.play();
        }
        await promise;
    }
    async #toggleBoostStream(isUserInitiated = true) {
        const { streams, boostStream } = this.streamData;
        this.#boostState.sessionId = uuidv4();
        const currentSwitchAttemptId = this.#boostState.sessionId;
        const currentListenId = this.#generateListenId();
        const shouldBailout = () => {
            return (currentSwitchAttemptId !== this.#boostState.sessionId ||
                currentListenId !== this.#listenId);
        };
        // user can switch back to directory station during boost stream or boost intro stream
        // also when ad break is active that means we are on directory stream (boost has no ads).
        const useDirectoryStream = player.isBoostOrIntroStream && !this.isAdBreak();
        const guideId = useDirectoryStream
            ? this.#guideItem?.guideId
            : this.#metadata.boost?.primary?.guideId;
        if (this.isAdBreak()) {
            player.stop();
        }
        this.#adPlayer.resetState();
        this.#isLoading = null;
        this.#playerReporter.stopSession(true);
        this.emit('loadstart');
        await this.#playBoostTransition();
        // id changes indicate a new load request has been made, so we should short-circuit
        if (shouldBailout()) {
            return;
        }
        this.emit('loadstart');
        this.#playerReporter.startSession({
            guideItem: { guideId, itemToken: this.#guideItem?.itemToken },
            listenId: this.#listenId,
            isUserInitiated,
        });
        this.#streamDataManager.sendBoostMetadata({
            isBoostOrIntroStream: !useDirectoryStream,
        });
        if (useDirectoryStream &&
            !this.#loadOptions?.skipPreroll &&
            this.#adPlayer.canPlayPreroll) {
            await this.#adPlayer.requestPreroll({ isReturningFromBoost: true });
            // id changes indicate a new load request has been made, so we should short-circuit
            if (shouldBailout()) {
                return;
            }
            this.emit('loadstart');
        }
        this.#playerReporter.onStartStreamLoad(useDirectoryStream ? streams : [boostStream]);
        await player.load(useDirectoryStream ? streams : boostStream);
    }
    /**
     * The next guide id is provided by the Tune API, but all it provides is a guide id string with no other metadata.
     * To have full functionality for the stream (e.g., reporting, favoriting, etc), we need the full guide item.
     * This method fetches the next guide item and loads it into the player. It also emits an event to the application
     * with the new guide item for UI handling.
     */
    async #playNextGuideId(guideId, isAutoPlayingNextStream = false) {
        const listenId = this.#listenId;
        const nextRootGuideItem = await api.ui.get({ guideId }).catch(noop);
        const guideItem = nextRootGuideItem?.containers?.[0]?.children?.[0];
        if (listenId !== this.#listenId) {
            return;
        }
        if (!guideItem) {
            this.#isLoading = null;
            this.#isFailed = true;
            this.stop();
            // TODO: consolidate PM-level error event creation
            this.emit('error', {
                error: { message: 'Stream data fetch failed', fatal: true },
                isLastSource: true,
            });
            return;
        }
        this.emit('nextguideitem', guideItem);
        await this.#load(guideItem, {
            isUserInitiated: !isAutoPlayingNextStream,
            isAutoPlayingNextStream,
        });
        if (this.isLoaded()) {
            await this.play();
        }
    }
    get muted() {
        return player.muted;
    }
    get guideItem() {
        return this.#guideItem;
    }
    get currentStream() {
        return player.currentSource;
    }
    get streamData() {
        return this.#streamDataManager.streamData;
    }
    get didPlayPreroll() {
        return player.didPlayPreroll;
    }
    get adMediaElement() {
        return player.adMediaElement;
    }
    get adContainerElement() {
        return player.adContainerElement;
    }
    get appReporter() {
        return this.#appReporter;
    }
    async init() {
        const { api: apiInstance, playerOptions, appSessionData, locale, localizations, enableFetchAutoplayGuideItem, debug = false, } = this.#options;
        api.init(apiInstance);
        toggleDebug(debug);
        appSessionDataProvider.init(appSessionData);
        await localizationsProvider.init({ locale, localizations });
        this.#setupWebMediaPlayer(playerOptions);
        propagateEvents({
            from: player.instance,
            to: this,
            exclude: ['metadata', 'error'],
        });
        this.#playerReporter = new PlayerReporter();
        this.#streamDataManager = new StreamDataManager({
            onMetadata: this.#onMetadata,
        });
        this.#adPlayer = new AdPlayer({
            // TODO: Make SSAI Manager accessible from WMP
            ssaiManager: this.#ssaiManager,
            streamDataManager: this.#streamDataManager,
        });
        this.#playerEventListeners = {
            ended: this.#onEnded,
            error: this.#onError,
        };
        this.#resetBoostState();
        this.#boostConfig = {
            introStreamSource: {
                url: getExperiment(BOOST_INTRO_AUDIO),
                isBoostIntroStream: true,
            },
            introMaxCount: getExperiment(BOOST_INTRO_PLAYSESSIONFREQCAP_COUNT, -1),
            outroStreamSource: {
                url: getExperiment(BOOST_OUTRO_AUDIO),
                isBoostOutroStream: true,
            },
            outroMaxCount: getExperiment(BOOST_OUTRO_PLAYSESSIONFREQCAP_COUNT, -1),
        };
        if (enableFetchAutoplayGuideItem) {
            const autoplayGuideItem = await this.#streamDataManager.fetchAutoplayGuideItem();
            if (autoplayGuideItem) {
                this.emit('autoplayguideitem', autoplayGuideItem);
            }
        }
    }
    setAppSessionData(appSessionData) {
        appSessionDataProvider.update(appSessionData);
    }
    setLocale(locale) {
        localizationsProvider.setLocale(locale);
    }
    async load(guideItem, options) {
        if (!this.isIdle() &&
            !this.isFailed() &&
            this.#transformGuideItem(guideItem).guideId === this.#guideItem?.guideId) {
            debug.warn('Ignoring duplicative load request for the same guideId', guideItem.guideId);
            return;
        }
        return this.#load(guideItem, options);
    }
    stop() {
        this.#isFailed = null;
        this.#isLoading = null;
        this.#listenId = 0;
        this.#deregisterEvents();
        player.stop();
        this.#adPlayer.resetState();
        this.#resetBoostState();
    }
    getEngineName() {
        return player.getEngineName();
    }
    getOptions() {
        return player.getOptions();
    }
    setOptions(options) {
        player.setOptions(options);
    }
    pause() {
        player.pause();
    }
    async play() {
        if (this.#guideItem && (this.isIdle() || this.isFailed())) {
            this.#resetBoostState();
            await this.#load(this.#guideItem);
            if (this.isFailed() || !this.isLoaded()) {
                return;
            }
        }
        await player.play();
    }
    hasNext() {
        if (this.streamData?.subscriptionRequiredStream) {
            return false;
        }
        return Boolean(this.streamData && (this.#metadata.next || this.#metadata.boost));
    }
    async playNext(isAutoPlayingNextStream = false) {
        if (!this.hasNext()) {
            return;
        }
        this.#streamDataManager.stopNowPlayingPoller();
        const { streams, boostStream } = this.streamData;
        const { nextGuideId } = streams[0] || {};
        if (nextGuideId) {
            await this.#playNextGuideId(nextGuideId, isAutoPlayingNextStream);
            return;
        }
        if (boostStream &&
            this.#metadata.boost &&
            getExperiment(BOOST_ENABLED)) {
            await this.#toggleBoostStream(!isAutoPlayingNextStream);
        }
        else {
            const boostPrimary = this.#metadata.boost?.primary;
            await this.#load({
                guideId: boostPrimary?.guideId || this.#metadata.next?.guideId || '',
                itemToken: this.#guideItem?.itemToken,
                ...(boostPrimary && {
                    next: this.#metadata.primary,
                }),
            });
        }
        if (this.isLoaded()) {
            await this.play();
        }
    }
    fetchMetadata(guideItem) {
        this.#streamDataManager?.fetchNowPlayingOnce(guideItem);
    }
    mute(value) {
        player.mute(value);
    }
    seekTo(seekTime) {
        if (this.isAdBreak()) {
            return;
        }
        this.#playerReporter.reportSeek({ seekTime });
        player.seekTo(seekTime);
    }
    seekBackward(seekOffset) {
        if (this.isAdBreak()) {
            return;
        }
        this.#playerReporter.reportSeek({ seekOffset: -seekOffset });
        player.seekBackward(seekOffset);
    }
    seekForward(seekOffset) {
        if (this.isAdBreak()) {
            return;
        }
        this.#playerReporter.reportSeek({ seekOffset });
        player.seekForward(seekOffset);
    }
    setVolume(value) {
        player.setVolume(value);
    }
    getPlaybackRate() {
        return player.getPlaybackRate();
    }
    setPlaybackRate(rate) {
        player.setPlaybackRate(rate);
    }
    isLoading() {
        if (this.isAdBreak()) {
            return player.isLoading();
        }
        return this.#isLoading ?? player.isLoading();
    }
    isLoaded() {
        return player.isLoaded();
    }
    isPlaying() {
        return player.isPlaying();
    }
    isPaused() {
        return player.isPaused();
    }
    isIdle() {
        return !this.#isLoading && !this.#isFailed && player.isIdle();
    }
    isFailed() {
        return (!this.#isLoading && this.#isFailed) ?? player.isFailed();
    }
    isAdBreak() {
        return player.isAdBreak;
    }
    isLive() {
        return player.isLive() || player.isBoostTransitionStream;
    }
    isLiveSeek() {
        return player.isLiveSeekStream;
    }
    getDuration() {
        return player.getDuration();
    }
    getCurrentTime() {
        return player.getCurrentTime();
    }
    getVolume() {
        return player.getVolume();
    }
    getTextTracks() {
        return player.getTextTracks();
    }
    getMediaElement() {
        return player.getMediaElement();
    }
    resizeVideoAd(width, height) {
        player.resizeVideoAd(width, height);
    }
}
